import { useQuery } from '@tanstack/react-query';
import { searchMyContacts, TAddPerson } from '../../requests/customerPortal';
import { btnPrimary, btnSecondary, inputClass } from '../Admin/ui';
import React, { useEffect, useState } from 'react';

export const fmtDate = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const fmtDateTime = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const fmtMoney = (v?: number | null) =>
  v == null
    ? '—'
    : v.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      });

export const fmtNum = (v?: number | null, digits = 0) =>
  v == null
    ? '—'
    : v.toLocaleString('en-US', { maximumFractionDigits: digits });

/** A KPI tile — label above, big number, optional caption. */
export const Stat: React.FC<{
  label: string;
  value: React.ReactNode;
  caption?: React.ReactNode;
}> = ({ label, value, caption }) => (
  <div className="rounded-xl border border-white-regular bg-white px-5 py-4">
    <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-two">
      {label}
    </div>
    <div className="mt-1 text-2xl font-semibold text-gray-five">{value}</div>
    {caption && <div className="mt-1 text-xs text-gray-bold">{caption}</div>}
  </div>
);

/** Used / invested as a thin bar. */
export const BalanceBar: React.FC<{
  used?: number | null;
  invested?: number | null;
}> = ({ used, invested }) => {
  if (!invested || invested <= 0) return null;
  const pct = Math.min(100, Math.max(0, ((used ?? 0) / invested) * 100));
  return (
    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-grey-100">
      <div
        className="h-full rounded-full bg-blue-primary"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

export const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-two">
      {label}
    </div>
    <div className="mt-0.5 text-sm text-gray-five">{children}</div>
  </div>
);

export const dateInputClass =
  'h-10 rounded-lg border border-white-regular bg-white px-3 text-sm text-gray-four focus:border-blue-primary focus:outline-none';

/** What a page shows when its query fails — instead of a spinner forever. */
export const LoadFailed: React.FC<{ error: unknown }> = ({ error }) => {
  const e = error as {
    response?: { status?: number; data?: { message?: string } };
    message?: string;
  };
  const status = e?.response?.status;
  const message = e?.response?.data?.message ?? e?.message ?? 'Unknown error';
  const hint =
    status === 404 || status === 403
      ? 'This login is not linked to a customer account. Staff: pick a customer in the preview bar above. Otherwise sign in as a customer manager.'
      : 'Try again in a moment. If it keeps happening, open a ticket.';
  return (
    <div className="mx-8 my-10 max-w-xl rounded-xl border border-white-regular bg-white px-6 py-8">
      <div className="text-sm font-semibold text-gray-five">
        We could not load this page
      </div>
      <div className="mt-1 text-sm text-gray-three">{hint}</div>
      <div className="mt-3 text-xs text-gray-bold">
        {status ? `${status} · ` : ''}
        {message}
      </div>
    </div>
  );
};

/** Retry network blips, never a 4xx — the answer will not change. */
export const retryUnless4xx = (failureCount: number, error: unknown) => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status && status >= 400 && status < 500) return false;
  return failureCount < 2;
};

/**
 * "Add one of my people": pick an existing person on the account, or give
 * a new name + email. Used by the private event page and the program page.
 */
export const AddPersonForm: React.FC<{
  label: string;
  busy?: boolean;
  allowWaitlist?: boolean;
  onSubmit: (body: TAddPerson) => void;
  onCancel: () => void;
}> = ({ label, busy, allowWaitlist, onSubmit, onCancel }) => {
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [contactId, setContactId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [waitlist, setWaitlist] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  const { data: hits = [] } = useQuery({
    queryKey: ['my', 'contacts', q],
    queryFn: () => searchMyContacts(q),
    enabled: mode === 'existing' && q.length >= 2,
  });
  const canSubmit =
    mode === 'existing'
      ? Boolean(contactId)
      : firstName.trim() && lastName.trim();
  return (
    <form
      className="rounded-xl border border-white-regular bg-white px-5 py-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        onSubmit(
          mode === 'existing'
            ? { contactId, waitlist: waitlist || undefined }
            : {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                email: email.trim() || undefined,
                waitlist: waitlist || undefined,
              },
        );
      }}
    >
      <div className="mb-3 flex items-center gap-3">
        <span className="text-sm font-semibold text-gray-five">{label}</span>
        <div className="flex gap-1">
          {(['existing', 'new'] as const).map((m) => (
            <button
              key={m}
              type="button"
              className={
                mode === m
                  ? 'rounded-lg bg-blue-100 px-3 py-1.5 text-sm font-semibold text-blue-primary'
                  : btnSecondary
              }
              onClick={() => setMode(m)}
            >
              {m === 'existing' ? 'Someone on my account' : 'A new person'}
            </button>
          ))}
        </div>
      </div>
      {mode === 'existing' ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <input
            className={inputClass}
            placeholder="Search by name or email"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setContactId('');
            }}
          />
          <select
            className={inputClass}
            value={contactId}
            onChange={(e) => setContactId(e.target.value)}
          >
            <option value="">
              {q.length < 2
                ? 'Type at least two letters'
                : hits.length
                ? 'Choose a person'
                : 'Nobody matches'}
            </option>
            {hits.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name ?? h.email ?? h.id}
                {h.email ? ` · ${h.email}` : ''}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-3">
          <input
            className={inputClass}
            placeholder="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <input
            className={inputClass}
            placeholder="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          <input
            className={inputClass}
            type="email"
            placeholder="Email (for reminders and the join link)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {allowWaitlist && (
          <label className="flex items-center gap-2 text-sm text-gray-four">
            <input
              type="checkbox"
              checked={waitlist}
              onChange={(e) => setWaitlist(e.target.checked)}
            />
            Add to the waiting list instead
          </label>
        )}
        <div className="ml-auto flex gap-2">
          <button type="button" className={btnSecondary} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="submit"
            className={btnPrimary}
            disabled={!canSubmit || busy}
          >
            {busy ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>
    </form>
  );
};
